import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Dispatch, bindActionCreators } from 'redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { RootState } from '../../reducers';
import * as actions from '../../actions/generalActions';
import * as offerActions from '../../actions/offers';
import { withRouter } from 'react-router';
import { useUrlFilters } from '../../hooks/useUrlFilters';

import MikaPageHeader from '../../components/MikaPageHeader/MikaPageHeader';
import MikaTable, { MikaColumn } from '../../components/MikaTable/MikaTable';
import MikaDateFilter, { DateRange } from '../../components/MikaDateFilter/MikaDateFilter';
import PaginationComponent from '../../components/Pagination/PaginationComponent';
import '../../components/Pagination/Pagination.scss';
import ContactDashboardStrip from '../Contact/ContactDashboardStrip';
import MikaViewToggle from '../../components/MikaViewToggle/MikaViewToggle';
import OfferCardGrid from './OfferCardGrid';
import MikaExport, { ExportColumn } from '../../components/MikaExport/MikaExport';
import { axiosCaller } from '../../utils/apiCaller';
import { formatPrice } from '../../utils/react';
import '../Contact/Contact.scss';

const isMobile = window.innerWidth < 576;
const VIEW_STORAGE_KEY = 'offersViewMode';

interface Props {
    intl: any;
    actions: any;
    offerActions: any;
    history: any;
    location: any;
    loading: boolean;
    buttonLoader: boolean;
    permissions: any;
    statusId1Content: any[];
    statusId2Content: any[];
    statusId3Content: any[];
    statusId4Content: any[];
    statusId5Content: any[];
    clientsByModuleData: any[];
    statusId1Pagination: any;
    statusId2Pagination: any;
    statusId3Pagination: any;
    statusId4Pagination: any;
    statusId5Pagination: any;
    pagination: any;
}

const STATUS_VISUAL: Record<number, { color: string; bg: string }> = {
    1: { color: '#ef4444', bg: '#fef2f2' },
    2: { color: '#22c55e', bg: '#dcfce7' },
    3: { color: '#3b82f6', bg: '#dbeafe' },
    4: { color: '#94a3b8', bg: '#f1f5f9' },
    5: { color: '#f59e0b', bg: '#fef3c7' },
};

const STATUS_I18N_KEY: Record<number, string> = {
    1: 'offer.status.rejected',
    2: 'offer.status.approved',
    3: 'offer.status.sent',
    4: 'offer.status.undefined',
    5: 'offer.status.pending',
};

const StatusCell: React.FC<{ status: any }> = ({ status }) => {
    const id = status?.value;
    const visual = STATUS_VISUAL[id] || { color: '#94a3b8', bg: '#f1f5f9' };
    const key = STATUS_I18N_KEY[id];
    return (
        <span style={{ color: visual.color, background: visual.bg, padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 500, display: 'inline-block' }}>
            {key
                ? <FormattedMessage id={key} defaultMessage={status?.label || '\u2014'} />
                : (status?.label || '\u2014')}
        </span>
    );
};

type StripFilter = 'total' | 'status1' | 'status2' | 'status3' | 'status5' | 'cities' | 'agents' | 'pocs';

const OffersListPage: React.FC<Props> = (props) => {
    const fm = (id: string, defaultMessage: string) => props.intl.formatMessage({ id, defaultMessage });

    const { filters, setFilter, setFilters, replaceFilters } = useUrlFilters(props.history, props.location, {
        page: { default: 1, type: 'number' },
        perPage: { default: 20, type: 'number' },
        status: { default: 'total', type: 'string' },
        search: { default: '', type: 'string' },
        dateFrom: { default: '', type: 'string' },
        dateTo: { default: '', type: 'string' },
        city: { default: '', type: 'string' },
        agentId: { default: '', type: 'string' },
        agentName: { default: '', type: 'string' },
        pocId: { default: '', type: 'string' },
        pocName: { default: '', type: 'string' },
    });

    const currentPage = filters.page;
    const perPage = filters.perPage;
    const searchTerm = filters.search;
    const stripFilter = filters.status as StripFilter;
    const dateRange: DateRange | null = (filters.dateFrom && filters.dateTo) ? { from: filters.dateFrom, to: filters.dateTo } : null;
    const activeCity: string | null = filters.city || null;
    const activeAgent: { id: number; name: string } | null = filters.agentId ? { id: Number(filters.agentId), name: filters.agentName } : null;
    const activePoc: { id: number; name: string } | null = filters.pocId ? { id: Number(filters.pocId), name: filters.pocName } : null;

    const setCurrentPage = (p: number) => setFilter('page', p);
    const setPerPage = (p: number) => setFilters({ perPage: p, page: 1 });
    const setSearchTerm = (s: string) => replaceFilters({ search: s, page: 1 });
    const setDateRange = (d: DateRange | null) => setFilters({ dateFrom: d?.from || '', dateTo: d?.to || '', page: 1 });

    const [statsData, setStatsData] = useState<any>(null);
    const [viewMode, setViewMode] = useState<'table' | 'card'>(() => {
        return (localStorage.getItem(VIEW_STORAGE_KEY) as 'table' | 'card') || 'table';
    });

    const [cityPopoverOpen, setCityPopoverOpen] = useState(false);
    const [agentPopoverOpen, setAgentPopoverOpen] = useState(false);
    const [pocPopoverOpen, setPocPopoverOpen] = useState(false);
    const [cityPopoverPos, setCityPopoverPos] = useState({ top: 0, right: 0 });
    const [agentPopoverPos, setAgentPopoverPos] = useState({ top: 0, right: 0 });
    const [pocPopoverPos, setPocPopoverPos] = useState({ top: 0, right: 0 });
    const cityRef = useRef<HTMLDivElement>(null);
    const agentRef = useRef<HTMLDivElement>(null);
    const pocRef = useRef<HTMLDivElement>(null);
    const stripRef = useRef<HTMLDivElement>(null);

    const columns: MikaColumn[] = useMemo(() => [
        {
            key: 'id',
            title: '#',
            sortable: true,
            width: '50px',
            render: (val: any) => <span style={{ color: '#94a3b8', fontSize: '11px' }}>{val}</span>
        },
        {
            key: 'clientName',
            title: fm('offer.list.column.client', 'Client'),
            sortable: true,
            render: (val: any, row: any) => `${val || ''} ${row.clientLastName || ''}`.trim() || '\u2014'
        },
        { key: 'project.label', title: fm('offer.list.column.project', 'Project') },
        {
            key: 'offerStatus',
            title: fm('offer.list.column.status', 'Status'),
            render: (_: any, row: any) => <StatusCell status={row.offerStatus} />,
        },
        { key: 'city', title: fm('offer.list.column.city', 'City'), sortable: true, hideOnMobile: true },
        {
            key: 'total',
            title: fm('offer.list.column.total', 'Total'),
            hideOnMobile: true,
            // `total` is the canonical figure from the backend: products + sub-products
            // + services, discount applied. Replaces the old products-only fallback.
            render: (val: any) => val != null ? formatPrice(val, '\u20AC') : '\u2014'
        },
        {
            key: 'firstUpdate',
            title: fm('offer.list.column.date', 'Date'),
            sortable: true,
            hideOnMobile: true,
            render: (val: any) => val || '\u2014'
        },
        {
            key: 'approvedAt',
            title: fm('offer.list.column.approvedOn', 'Approved on'),
            sortable: true,
            hideOnMobile: true,
            render: (val: any) => val || '\u2014'
        },
        {
            key: 'user.userName',
            title: fm('offer.list.column.agent', 'Agent'),
            hideOnMobile: true,
            render: (val: any) => val || '\u2014'
        },
        {
            key: 'pointOfContact.name',
            title: fm('offer.list.column.responsible', 'Responsible'),
            hideOnMobile: true,
            render: (val: any) => val || '\u2014'
        }
    ], [props.intl]);

    const getStatusId = (strip: StripFilter): string | undefined => {
        if (strip === 'status1') return '1';
        if (strip === 'status2') return '2';
        if (strip === 'status3') return '3';
        if (strip === 'status5') return '5';
        return undefined;
    };

    const fetchData = () => {
        const statusId = getStatusId(stripFilter);
        const search = [activeCity, searchTerm].filter(Boolean).join(' ');
        const userId = activeAgent?.id ? String(activeAgent.id) : '';
        const pocUserId = activePoc?.id ? String(activePoc.id) : '';
        props.actions.getClientsForOffer(
            statusId,
            dateRange?.from || undefined,
            dateRange?.to || undefined,
            currentPage,
            perPage,
            search,
            userId,
            pocUserId
        );
    };

    useEffect(() => {
        props.offerActions.getOfferDashboardStats(dateRange?.from || undefined, dateRange?.to || undefined).then((res: any) => {
            if (res) setStatsData(res);
        });
    }, [dateRange?.from, dateRange?.to]);

    useEffect(() => {
        fetchData();
    }, [props.location.search]);

    const getCurrentData = () => {
        if (stripFilter === 'status1') return props.statusId1Content || [];
        if (stripFilter === 'status2') return props.statusId2Content || [];
        if (stripFilter === 'status3') return props.statusId3Content || [];
        if (stripFilter === 'status5') return props.statusId5Content || [];
        return props.clientsByModuleData || [];
    };

    const getCurrentPagination = () => {
        if (stripFilter === 'status1') return props.statusId1Pagination;
        if (stripFilter === 'status2') return props.statusId2Pagination;
        if (stripFilter === 'status3') return props.statusId3Pagination;
        if (stripFilter === 'status5') return props.statusId5Pagination;
        return props.pagination;
    };

    const data = getCurrentData();
    const pagination = getCurrentPagination();

    const dashboardStats = useMemo(() => {
        const s = statsData;
        return [
            { key: 'total', label: fm('offer.dashboard.totalOffers', 'Total'), value: s?.total ?? pagination?.total ?? 0, icon: 'users' },
            { key: 'status5', label: fm('offer.dashboard.onHold', 'On hold'), value: s?.status5 ?? '\u2014', icon: 'alert' },
            { key: 'status3', label: fm('offer.dashboard.sent', 'Sent'), value: s?.status3 ?? '\u2014', icon: 'folder' },
            { key: 'status2', label: fm('offer.dashboard.approved', 'Approved'), value: s?.status2 ?? '\u2014', icon: 'userPlus' },
            { key: 'status1', label: fm('offer.dashboard.rejected', 'Rejected'), value: s?.status1 ?? '\u2014', icon: 'alert' },
            { key: 'cities', label: fm('offer.dashboard.cities', 'Cities'), value: Object.keys(s?.cityDistribution || {}).length || '\u2014', icon: 'map' },
            { key: 'agents', label: fm('offer.dashboard.agents', 'Agents'), value: (s?.agentDistribution || []).length || '\u2014', icon: 'agent' },
            { key: 'pocs', label: fm('offer.dashboard.responsibles', 'Responsibles'), value: (s?.pocDistribution || []).length || '\u2014', icon: 'userPlus' },
        ];
    }, [statsData, pagination, props.intl]);

    const cityDistribution = useMemo(() => {
        const dist = statsData?.cityDistribution;
        if (!dist) return [];
        return Object.entries(dist).sort((a: any, b: any) => b[1] - a[1]);
    }, [statsData]);

    const agentDistribution: { id: number; name: string; count: number }[] = useMemo(() => {
        return statsData?.agentDistribution || [];
    }, [statsData]);

    const pocDistribution: { id: number; name: string; count: number }[] = useMemo(() => {
        return statsData?.pocDistribution || [];
    }, [statsData]);

    const positionPopoverUnderCard = (cardIndex: number) => {
        if (!stripRef.current) return { top: 0, right: 0 };
        const cards = stripRef.current.querySelectorAll('.cds-card');
        const card = cards[cardIndex];
        if (!card) return { top: 0, right: 0 };
        const rect = card.getBoundingClientRect();
        const popoverWidth = 220;
        const cardCenter = rect.left + rect.width / 2;
        return { top: rect.bottom + 4, right: window.innerWidth - cardCenter - popoverWidth / 2 };
    };

    const handleStripClick = (key: string) => {
        if (key === 'cities') {
            setAgentPopoverOpen(false);
            setPocPopoverOpen(false);
            if (!cityPopoverOpen) setCityPopoverPos(positionPopoverUnderCard(5));
            setCityPopoverOpen(!cityPopoverOpen);
            return;
        }
        if (key === 'agents') {
            setCityPopoverOpen(false);
            setPocPopoverOpen(false);
            if (!agentPopoverOpen) setAgentPopoverPos(positionPopoverUnderCard(6));
            setAgentPopoverOpen(!agentPopoverOpen);
            return;
        }
        if (key === 'pocs') {
            setCityPopoverOpen(false);
            setAgentPopoverOpen(false);
            if (!pocPopoverOpen) setPocPopoverPos(positionPopoverUnderCard(7));
            setPocPopoverOpen(!pocPopoverOpen);
            return;
        }
        setCityPopoverOpen(false);
        setAgentPopoverOpen(false);
        setPocPopoverOpen(false);
        const newFilter = key as StripFilter;
        setFilters({
            status: newFilter === stripFilter ? 'total' : newFilter,
            page: 1,
            search: '',
            city: '',
            agentId: '',
            agentName: '',
            pocId: '',
            pocName: '',
        });
    };

    const handleCityFilter = (city: string) => { setFilters({ city: city, agentId: '', agentName: '', pocId: '', pocName: '', status: 'cities', page: 1 }); setCityPopoverOpen(false); };
    const handleAgentFilter = (agent: { id: number; name: string }) => { setFilters({ agentId: String(agent.id), agentName: agent.name, city: '', pocId: '', pocName: '', status: 'agents', page: 1 }); setAgentPopoverOpen(false); };
    const handlePocFilter = (poc: { id: number; name: string }) => { setFilters({ pocId: String(poc.id), pocName: poc.name, city: '', agentId: '', agentName: '', status: 'pocs', page: 1 }); setPocPopoverOpen(false); };
    const clearCityFilter = () => { setFilters({ city: '', status: 'total', page: 1 }); };
    const clearAgentFilter = () => { setFilters({ agentId: '', agentName: '', status: 'total', page: 1 }); };
    const clearPocFilter = () => { setFilters({ pocId: '', pocName: '', status: 'total', page: 1 }); };

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (cityRef.current && !cityRef.current.contains(e.target as Node)) setCityPopoverOpen(false);
            if (agentRef.current && !agentRef.current.contains(e.target as Node)) setAgentPopoverOpen(false);
            if (pocRef.current && !pocRef.current.contains(e.target as Node)) setPocPopoverOpen(false);
        };
        if (cityPopoverOpen || agentPopoverOpen || pocPopoverOpen) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [cityPopoverOpen, agentPopoverOpen, pocPopoverOpen]);

    const handleViewChange = (mode: 'table' | 'card') => {
        setViewMode(mode);
        localStorage.setItem(VIEW_STORAGE_KEY, mode);
    };

    const effectiveView = isMobile ? 'table' : viewMode;

    const editOffer = (row: any) => {
        props.history.push({ pathname: `/dashboard/offers/create/${row.id}/${row.projectId}` });
    };

    const exportColumns: ExportColumn[] = [
        { key: 'id', title: '#' },
        { key: 'clientName', title: fm('offer.list.column.client', 'Client'), render: (r) => `${r.clientName || ''} ${r.clientLastName || ''}`.trim() },
        { key: 'project.label', title: fm('offer.list.column.project', 'Project') },
        {
            key: 'offerStatus',
            title: fm('offer.list.column.status', 'Status'),
            render: (r) => {
                const id = r?.offerStatus?.value;
                const key = STATUS_I18N_KEY[id];
                return key ? fm(key, r?.offerStatus?.label || '') : (r?.offerStatus?.label || '');
            }
        },
        { key: 'city', title: fm('offer.list.column.city', 'City') },
        { key: 'total', title: fm('offer.list.column.total', 'Total'), render: (r) => r.total != null ? formatPrice(r.total) : '' },
        { key: 'firstUpdate', title: fm('offer.list.column.date', 'Date') },
        { key: 'approvedAt', title: fm('offer.list.column.approvedOn', 'Approved on') },
        { key: 'user.userName', title: fm('offer.list.column.agent', 'Agent') },
        { key: 'pointOfContact.name', title: fm('offer.list.column.responsible', 'Responsible') },
    ];

    const fetchAllForExport = async (): Promise<any[]> => {
        const statusId = getStatusId(stripFilter);
        const search = [activeCity, searchTerm].filter(Boolean).join(' ');
        const userId = activeAgent?.id ? String(activeAgent.id) : '';
        const pocUserId = activePoc?.id ? String(activePoc.id) : '';
        let queryParams: string[] = [];
        if (statusId) queryParams.push(`offerStatusId=${statusId}`);
        if (dateRange?.from && dateRange?.to) {
            queryParams.push(`startDate=${dateRange.from}`);
            queryParams.push(`endDate=${dateRange.to}`);
        }
        queryParams.push('per_page=0');
        if (search) queryParams.push(`search=${encodeURIComponent(search)}`);
        if (userId) queryParams.push(`user_id=${userId}`);
        if (pocUserId) queryParams.push(`poc_user_id=${pocUserId}`);
        const qs = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
        const res = await axiosCaller('GET', `offer/filter${qs}`);
        return res?.data?.data || [];
    };

    const offersTitle = fm('offer.list.title', 'Offers');
    const exportTitle = stripFilter === 'status1' ? fm('offer.list.exportTitle.rejected', 'Rejected offers')
        : stripFilter === 'status2' ? fm('offer.list.exportTitle.approved', 'Approved offers')
        : stripFilter === 'status3' ? fm('offer.list.exportTitle.sent', 'Sent offers')
        : stripFilter === 'status5' ? fm('offer.list.exportTitle.pending', 'Pending offers')
        : activeCity ? `${offersTitle} - ${activeCity}`
        : activeAgent ? `${offersTitle} - ${activeAgent.name}`
        : activePoc ? `${offersTitle} - ${activePoc.name}`
        : offersTitle;

    const tableActions = [
        {
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>,
            tooltip: fm('common.action.edit', 'Edit'),
            onClick: editOffer
        }
    ];

    return (
        <div className="clp-page">
            {!isMobile && (
                <MikaPageHeader
                    title={offersTitle}
                    subtitle={pagination ? `${pagination.total} total` : undefined}
                />
            )}

            <div className="clp-compact-toolbar">
                <div className="clp-search-wrap">
                    <svg className="clp-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
                    </svg>
                    <input type="text" className="clp-search-input" placeholder={fm('offer.list.searchPlaceholder', 'Search offers...')}
                        value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    {searchTerm && (
                        <button className="clp-search-clear" onClick={() => setSearchTerm('')} type="button">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                        </button>
                    )}
                </div>
                <div className="clp-toolbar-actions">
                    <MikaDateFilter value={dateRange} onChange={(r) => setDateRange(r)} />
                    {!isMobile && <MikaViewToggle mode={viewMode} onChange={handleViewChange} />}
                    <MikaExport
                        fetchAllData={fetchAllForExport}
                        columns={exportColumns}
                        fileName={fm('offer.list.exportFilename', 'offers')}
                        title={exportTitle}
                        companyProfile={(props as any).companyProfile}
                        sumColumn={{
                            getValue: (row) => {
                                const v = row.discountTotal != null ? row.discountTotal : row.offerTotalCalculation;
                                return v ? Number(v) : 0;
                            },
                            label: fm('offer.dashboard.totalSum', 'Total:'),
                            suffix: '\u20AC',
                        }}
                    />
                </div>
            </div>

            <div ref={stripRef}>
                        <ContactDashboardStrip stats={dashboardStats} activeFilter={stripFilter} onFilterClick={handleStripClick} />
                    </div>
                    {cityPopoverOpen && (
                        <div className="clp-city-popover" ref={cityRef} style={{ top: cityPopoverPos.top, right: cityPopoverPos.right }}>
                            <div className="clp-city-header"><FormattedMessage id="offer.dashboard.topCities" defaultMessage="Top cities" /></div>
                            {cityDistribution.length === 0 ? <div className="clp-city-empty"><FormattedMessage id="common.noData" defaultMessage="No data available" /></div> :
                                cityDistribution.map(([city, count]) => (
                                    <button key={city} className={`clp-city-row ${activeCity === city ? 'clp-city-row-active' : ''}`} onClick={() => handleCityFilter(city)} type="button">
                                        <span className="clp-city-name">{city}</span><span className="clp-city-count">{count}</span>
                                    </button>
                                ))
                            }
                        </div>
                    )}
                    {agentPopoverOpen && (
                        <div className="clp-city-popover" ref={agentRef} style={{ top: agentPopoverPos.top, right: agentPopoverPos.right }}>
                            <div className="clp-city-header"><FormattedMessage id="offer.dashboard.agents" defaultMessage="Agents" /></div>
                            {agentDistribution.length === 0 ? <div className="clp-city-empty"><FormattedMessage id="common.noData" defaultMessage="No data available" /></div> :
                                agentDistribution.map((agent) => (
                                    <button key={agent.id} className={`clp-city-row ${activeAgent?.id === agent.id ? 'clp-city-row-active' : ''}`} onClick={() => handleAgentFilter(agent)} type="button">
                                        <span className="clp-city-name">{agent.name}</span><span className="clp-city-count">{agent.count}</span>
                                    </button>
                                ))
                            }
                        </div>
                    )}
                    {pocPopoverOpen && (
                        <div className="clp-city-popover" ref={pocRef} style={{ top: pocPopoverPos.top, right: pocPopoverPos.right }}>
                            <div className="clp-city-header"><FormattedMessage id="offer.dashboard.responsibles" defaultMessage="Responsibles" /></div>
                            {pocDistribution.length === 0 ? <div className="clp-city-empty"><FormattedMessage id="common.noData" defaultMessage="No data available" /></div> :
                                pocDistribution.map((poc) => (
                                    <button key={poc.id} className={`clp-city-row ${activePoc?.id === poc.id ? 'clp-city-row-active' : ''}`} onClick={() => handlePocFilter(poc)} type="button">
                                        <span className="clp-city-name">{poc.name}</span><span className="clp-city-count">{poc.count}</span>
                                    </button>
                                ))
                            }
                        </div>
                    )}

            {(activeCity || activeAgent || activePoc) && (
                <div className="clp-active-filter-bar">
                    {activeCity && (
                        <div className="clp-active-filter-chip">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>
                            <span>{activeCity}</span>
                            <span className="clp-active-filter-count">{pagination?.total ?? 0} <FormattedMessage id="offer.list.records" defaultMessage="offers" /></span>
                            <button className="clp-active-filter-clear" onClick={clearCityFilter} type="button"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12" /></svg></button>
                        </div>
                    )}
                    {activeAgent && (
                        <div className="clp-active-filter-chip">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                            <span>{activeAgent.name}</span>
                            <span className="clp-active-filter-count">{pagination?.total ?? 0} <FormattedMessage id="offer.list.records" defaultMessage="offers" /></span>
                            <button className="clp-active-filter-clear" onClick={clearAgentFilter} type="button"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12" /></svg></button>
                        </div>
                    )}
                    {activePoc && (
                        <div className="clp-active-filter-chip">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="8.5" cy="7" r="4" /><path d="M20 8v6M23 11h-6" /></svg>
                            <span>{activePoc.name}</span>
                            <span className="clp-active-filter-count">{pagination?.total ?? 0} <FormattedMessage id="offer.list.records" defaultMessage="offers" /></span>
                            <button className="clp-active-filter-clear" onClick={clearPocFilter} type="button"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12" /></svg></button>
                        </div>
                    )}
                </div>
            )}

            {effectiveView === 'table'
                ? <MikaTable columns={columns} data={data} actions={tableActions} loading={props.loading} onRowClick={editOffer} />
                : <OfferCardGrid data={data} onEdit={editOffer} />
            }

            <PaginationComponent
                currentPage={currentPage} perPage={perPage} pagination={pagination}
                onPageChange={(p) => setCurrentPage(p)} onPerPageChange={(v) => setPerPage(v)}
                label={fm('offer.list.records', 'offers')}
            />
        </div>
    );
};

function mapStateToProps(state: RootState, ownProps: any) {
    return { ...ownProps, ...state.app, offers: state.offers };
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
    return {
        actions: bindActionCreators(actions as any, dispatch),
        offerActions: bindActionCreators(offerActions as any, dispatch),
    };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(OffersListPage as any)));
